"use client";

import { useState, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { useUser } from "./contexts/UserContext";
import { API_URL } from "./config";
import "./App.css";

function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user, login } = useUser();

  useEffect(() => {
    // Redirect based on user role if already logged in
    if (user) {
      if (user.role === 'instructor') {
        navigate("/teacher");
      } else {
        navigate("/assessments");
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = isRegister ? "/api/register" : "/api/login";
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        credentials: "include", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      setMessage(data.message);
    } catch (error) {
      setMessage("Error connecting to server");
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        // Get user info from Google
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        const userInfo = await userInfoResponse.json();

        // Send token to your backend for verification and user creation/login
        const response = await fetch(`${API_URL}/api/google-auth`, {
          method: "POST",
          credentials: "include", 
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: tokenResponse.access_token,
            userInfo,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          // Use the context's login function instead of directly manipulating localStorage
          login(data.user, data.access_token);

          // Redirect based on user role
          if (data.user.role === 'instructor') {
            navigate("/teacher");
          } else {
            navigate("/assessments");
          }
        } else {
          setMessage(data.error || "Failed to authenticate with Google");
        }
      } catch (error) {
        console.error("Error during Google login:", error);
        setMessage("Error authenticating with Google");
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setMessage("Google login failed");
    },
  });

  return (
    <div className="login-page-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            {isRegister ? "Create an Account" : "Welcome Back"}
          </h2>
          <p className="card-description">
            {isRegister
              ? "Sign up for a new account to track your progress"
              : "Sign in to your account to continue your learning journey"}
          </p>
        </div>

        <div className="card-content">
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <input
                id="username"
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>

            <button
              type="submit"
              className="button button-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <span>
                  <span className="spinner"></span>
                  {isRegister ? "Creating account..." : "Signing in..."}
                </span>
              ) : isRegister ? (
                "Register"
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="divider">
            <span>OR</span>
          </div>

          <button
            onClick={googleLogin}
            className="button button-google"
            disabled={isLoading}
          >
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google logo"
              className="google-icon"
            />
            Sign in with Google
          </button>

          {message && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <p className="alert-message">{message}</p>
            </div>
          )}
        </div>

        <div className="card-footer">
          <button
            className="button button-text"
            onClick={() => setIsRegister(!isRegister)}
          >
            {isRegister
              ? "Already have an account? Sign in"
              : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
